import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import productionContactsService from '../services/productionContactsService';
import {
  Phone, Calendar, X, Loader, Plus, Trash2, Clock, User
} from 'lucide-react';
import toast from 'react-hot-toast';

const CONTACT_TYPES = [
  { value: 'PREVISAO_MONTAGEM', label: 'Previsão de Montagem' },
  { value: 'CONFIRMACAO', label: 'Confirmação' },
  { value: 'ALTERACAO', label: 'Alteração de Prazos' },
  { value: 'DUVIDA', label: 'Dúvida/Suporte' },
  { value: 'OUTROS', label: 'Outros' }
];

const ProductionContactModal = ({ projectId, projectName, clientName, onClose }) => {
  const queryClient = useQueryClient();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  // Buscar contatos
  const { data: contactsData, isLoading } = useQuery({
    queryKey: ['production-contacts', projectId],
    queryFn: () => productionContactsService.getContacts(projectId),
    enabled: !!projectId
  });

  const contacts = contactsData?.data || [];

  // Criar contato
  const createMutation = useMutation({
    mutationFn: (data) => productionContactsService.createContact(projectId, data),
    onSuccess: () => {
      toast.success('Contato registrado!');
      queryClient.invalidateQueries(['production-contacts', projectId]);
      setShowCreateForm(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao registrar');
    }
  });

  // Deletar contato
  const deleteMutation = useMutation({
    mutationFn: (contactId) => productionContactsService.deleteContact(contactId),
    onSuccess: () => {
      toast.success('Contato deletado!');
      queryClient.invalidateQueries(['production-contacts', projectId]);
      setSelectedContact(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao deletar');
    }
  });

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Phone className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Contatos de Produção</h2>
              <p className="text-sm text-gray-600">{projectName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Lista de Contatos */}
        <div className="flex h-[50vh]">
          <div className="w-1/2 border-r p-4 overflow-y-auto">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-medium text-gray-700">Histórico de Contatos</h3>
              <button
                onClick={() => setShowCreateForm(true)}
                className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
              >
                <Plus className="w-4 h-4" />
                Novo
              </button>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center p-4">
                <Loader className="w-5 h-5 animate-spin text-blue-600" />
              </div>
            ) : contacts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Phone className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p>Nenhum contato registrado.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {contacts.map((contact) => (
                  <div
                    key={contact.id}
                    onClick={() => setSelectedContact(contact)}
                    className={`p-3 border rounded cursor-pointer ${
                      selectedContact?.id === contact.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">
                        {CONTACT_TYPES.find(t => t.value === contact.contact_type)?.label || contact.contact_type}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatDate(contact.contact_date)}
                      </span>
                    </div>
                    {contact.next_action_date && (
                      <div className="flex items-center gap-1 text-xs text-orange-600 mt-1">
                        <Clock className="w-3 h-3" />
                        Próxima ação: {formatDate(contact.next_action_date)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Detalhes do Contato */}
          <div className="w-1/2 p-4 overflow-y-auto">
            {!selectedContact ? (
              <div className="text-center py-8 text-gray-500">
                <User className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p>Selecione um contato para ver detalhes</p>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">
                    {CONTACT_TYPES.find(t => t.value === selectedContact.contact_type)?.label || 'Contato'}
                  </h3>
                  <button
                    onClick={() => deleteMutation.mutate(selectedContact.id)}
                    disabled={deleteMutation.isPending}
                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500">Data do Contato</p>
                    <p className="text-sm font-medium">{formatDate(selectedContact.contact_date)}</p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500">Registrado por</p>
                    <p className="text-sm">{selectedContact.created_by_name || '-'}</p>
                  </div>

                  {selectedContact.notes && (
                    <div>
                      <p className="text-xs text-gray-500">Observações</p>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedContact.notes}</p>
                    </div>
                  )}

                  {selectedContact.next_action && (
                    <div>
                      <p className="text-xs text-gray-500">Próxima Ação</p>
                      <p className="text-sm font-medium text-blue-600">{selectedContact.next_action}</p>
                    </div>
                  )}

                  {selectedContact.next_action_date && (
                    <div>
                      <p className="text-xs text-gray-500">Data da Próxima Ação</p>
                      <p className="text-sm font-medium text-orange-600">{formatDate(selectedContact.next_action_date)}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300">
            Fechar
          </button>
        </div>

        {/* Modal de Criação */}
        {showCreateForm && (
          <CreateContactForm
            onClose={() => setShowCreateForm(false)}
            onSubmit={(data) => createMutation.mutate(data)}
            isPending={createMutation.isPending}
          />
        )}
      </div>
    </div>
  );
};

// Formulário de criação
const CreateContactForm = ({ onClose, onSubmit, isPending }) => {
  const [contactDate, setContactDate] = useState(new Date().toISOString().split('T')[0]);
  const [contactType, setContactType] = useState('PREVISAO_MONTAGEM');
  const [notes, setNotes] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [nextActionDate, setNextActionDate] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      contact_date: contactDate,
      contact_type: contactType,
      notes,
      next_action: nextAction || null,
      next_action_date: nextActionDate || null
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Novo Contato</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data do Contato</label>
            <input
              type="date"
              value={contactDate}
              onChange={(e) => setContactDate(e.target.value)}
              className="w-full border rounded px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Contato</label>
            <select
              value={contactType}
              onChange={(e) => setContactType(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              {CONTACT_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full border rounded px-3 py-2"
              rows={3}
              placeholder="Detalhes do contato..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Próxima Ação</label>
            <input
              type="text"
              value={nextAction}
              onChange={(e) => setNextAction(e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="Ex: Confirmar data de montagem"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data da Próxima Ação</label>
            <input
              type="date"
              value={nextActionDate}
              onChange={(e) => setNextActionDate(e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isPending ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Salvar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductionContactModal;